import { Calendar } from '@berlitz/icons'
import PageTitle from '@components/PageTitle'
import PageWithRightSidebarTpl from '@components/PageWithRIghtSidebarTpl'
import WithPortalAuthorization from '@hocs/WithPortalAuthorization'
import WithUserTime, { WithUserTimeProps } from '@hocs/WithUserTime'
import EventOrientationProvider from '@layouts/Events/context/EventOrientation'
import QueryMyCalendar from '@layouts/Scheduling/MyCalendar/QueryMyCalendar'
import MyCalendarSidebar from '@layouts/Scheduling/MyCalendarSidebar'
import ScheduleList from '@layouts/Scheduling/ScheduleList'
import SchedulingLayout from '@layouts/Scheduling/SchedulingLayout'
import { convertMomentToDate } from '@utils/helpers'
import { UserProfileProps } from 'interfaces/UserProfile'
import moment from 'moment'
import React from 'react'

interface MyCalendarProps {
  profile: UserProfileProps
}
interface MyCalendarState {
  startDate: string
  endDate: string
  openToDate: any
  sidebarStartDate: string
  sidebarEndDate: string
  filterType: string
  limit: number
  selectedDate: moment.Moment
  selectedMonth: string
  selected: Date | null
  displayPastEvents: boolean
}

const SCHEDULES_QUERY_LIMIT = 1000

class MyCalendar extends React.Component<MyCalendarProps & WithUserTimeProps, MyCalendarState> {
  constructor(props) {
    super(props)
    this.state = {
      startDate: moment.utc(props.getUserDate().startOf('day')).format(),
      endDate: moment.utc(props.getUserDate().endOf('month').endOf('week')).format(),
      openToDate: new Date(moment.parseZone(props.getUserDate()).startOf('month').format('YYYY/MM/DD')),
      sidebarStartDate: moment.utc(props.getUserDate().startOf('month')).subtract(7, 'days').format(),
      sidebarEndDate: moment.utc(props.getUserDate().endOf('month')).add(7, 'days').format(),
      filterType: 'FILTER_STUDENT_MY_CALENDER',
      limit: SCHEDULES_QUERY_LIMIT,
      selectedDate: props.getUserDate().startOf('day'),
      selectedMonth: props.getUserDate().format('MMMM'),
      selected: null,
      displayPastEvents: false,
    }
  }

  onViewMonth = () =>
    this.setState({
      startDate: moment.utc(moment(this.state.startDate).startOf('month').startOf('week')).format(),
      endDate: moment.utc(moment(this.state.endDate).endOf('month').endOf('week')).format(),
      selected: null,
      displayPastEvents: true,
    })

  onPicker = (date) => {
    const dateOnUserTimeZone = this.props.getDateWithUserTimezone(moment(date).format('YYYY-MM-DD'))

    this.setState({
      startDate: moment.utc(dateOnUserTimeZone.startOf('day')).format(),
      selectedDate: moment(dateOnUserTimeZone).startOf('day'),
      selected: convertMomentToDate(dateOnUserTimeZone),
      displayPastEvents: true,
    })
  }

  onPickerSelect = (date) => {
    const dateOnUserTimeZone = this.props.getDateWithUserTimezone(moment(date).format('YYYY-MM-DD'))
    if (this.state.selectedMonth !== dateOnUserTimeZone.format('MMMM')) {
      return this.onMonthChange(date, date)
    }

    if (this.state.selectedDate.isSame(dateOnUserTimeZone)) {
      this.setState({
        startDate: moment.utc(dateOnUserTimeZone.startOf('month')).format(),
        // endDate: moment.utc(dateOnUserTimeZone.endOf('day')).format(),
        selectedDate: this.props.getUserDate().startOf('month'),
        selected: null,
        displayPastEvents: true,
      })
    }
  }

  onMonthChange = (date, selected?) => {
    const dateOnUserTimeZone = this.props.getDateWithUserTimezone(moment(date).format('YYYY-MM-DD'))
    const startDate = moment.utc(dateOnUserTimeZone.clone().startOf('month')).format()
    const endDate = moment.utc(dateOnUserTimeZone.clone().endOf('month').endOf('week')).format()
    const sideBarStartDate = moment.utc(dateOnUserTimeZone.clone().startOf('month')).subtract(7, 'days').format()
    const sideBarEndDate = moment.utc(dateOnUserTimeZone.clone().endOf('month')).add(7, 'days').format()

    this.setState({
      openToDate: new Date(moment.parseZone(dateOnUserTimeZone.clone()).startOf('month').format('YYYY/MM/DD')),
      startDate: selected || startDate,
      endDate,
      sidebarStartDate: sideBarStartDate,
      sidebarEndDate: sideBarEndDate,
      selectedMonth: dateOnUserTimeZone.clone().format('MMMM'),
      selected,
      displayPastEvents: true,
    })
  }

  // Force refetch with different parameters to force re-render
  refetch = () => {
    this.setState({
      limit: this.state.limit === SCHEDULES_QUERY_LIMIT ? SCHEDULES_QUERY_LIMIT + 1 : SCHEDULES_QUERY_LIMIT,
    })
  }

  render() {
    const {
      filterType,
      limit,
      sidebarStartDate,
      sidebarEndDate,
      openToDate,
      selectedDate,
      selectedMonth,
      selected,
      displayPastEvents,
    } = this.state
    const { profile } = this.props
    return (
      <>
        <PageTitle title="My Calendar" />
        <QueryMyCalendar
          Id={profile.SFId}
          FromDate={sidebarStartDate}
          EndDate={sidebarEndDate}
          FilterType={filterType}
          Limit={limit}
          UserId={profile.SFId}
        >
          {({ loading, data }) => {
            return (
              <PageWithRightSidebarTpl
                body={
                  <SchedulingLayout activeTab={2} withSidebar showClock>
                    <EventOrientationProvider>
                      <ScheduleList
                        refetch={this.refetch}
                        onViewMonth={this.onViewMonth}
                        availableSchedules={data}
                        variables={{
                          Id: profile.SFId,
                          FromDate: sidebarStartDate,
                          EndDate: sidebarEndDate,
                          FilterType: filterType,
                          Limit: limit,
                          UserId: profile.SFId
                        }}
                        selectedDate={selectedDate}
                        selected={selected}
                        loading={loading}
                        selectedMonth={selectedMonth}
                        onMonthChange={this.onMonthChange}
                        displayPastEvents={displayPastEvents}
                      />
                    </EventOrientationProvider>
                  </SchedulingLayout>
                }
                sidebar={
                  <MyCalendarSidebar
                    onPicker={this.onPicker}
                    onPickerSelect={this.onPickerSelect}
                    onMonthChange={this.onMonthChange}
                    availableSchedules={data}
                    openToDate={openToDate}
                    loading={loading}
                    selected={selected}
                    variables={{
                      Id: profile.SFId,
                      FromDate: sidebarStartDate,
                      EndDate: sidebarEndDate,
                      FilterType: filterType,
                      Limit: limit,
                      UserId: profile.SFId
                    }}
                  />
                }
                toggleIcon={<Calendar size="md" />}
              />
            )
          }}
        </QueryMyCalendar>
      </>
    )
  }
}

export default WithPortalAuthorization(WithUserTime(MyCalendar), {
  id: 'page::scheduling::my-calendar',
})
